import { isValidElement, type JSX, type ReactNode } from 'react';
import { CopyButton } from '../../../components/ui/copy-button/copy-button.js';
import { cn } from '../../../lib/cn.js';

// Block code surface, matching the getting-started page: a #0c0c0c panel with a
// hairline border and a ghost CopyButton. Mapped from MDX `pre` (whose child is
// the `<code>` element); the inline-vs-block `code` distinction is handled in
// the MDX components map.

function nodeText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement(node)) {
    return nodeText((node.props as { children?: ReactNode }).children);
  }
  return '';
}

export interface CodeBlockProps {
  children?: ReactNode;
  className?: string;
}

export function CodeBlock({ children, className }: CodeBlockProps): JSX.Element {
  const code = nodeText(children).replace(/\n$/, '');

  return (
    <div className={cn('group relative my-5', className)}>
      <pre className="m-0 overflow-x-auto rounded-md border border-white/[0.06] bg-[#0c0c0c] px-4 py-3.5 font-mono text-[13px] leading-[1.65] text-primary">
        {children}
      </pre>
      <CopyButton
        text={code}
        variant="ghost"
        icon="clipboard"
        label=""
        copiedLabel=""
        aria-label="Copy code"
        className="absolute right-2 top-2 px-1.5 py-1 text-muted opacity-0 transition-opacity hover:text-secondary focus-visible:opacity-100 group-hover:opacity-100"
      />
    </div>
  );
}
